import {
  FaceOffBox,
  FaceOffFace,
  FaceOffRunFace,
  FaceOffRunFaceChange,
} from '../../../../services/coreApi/faceoff'
import copy from './copy.json'

// The Rekognition call each scan path makes, which is what can drift.
export const SCAN_NAME: Record<string, string> = {
  publish: 'SearchUsersByImage',
  retroactive: 'SearchFaces',
  cross_camp: 'SearchUsersByImage (other camp)',
}

export const scanName = (path: string): string => SCAN_NAME[path] ?? path

/** A Rekognition score, one decimal, as the local Face Off app shows it. */
export const formatScore = (score: number | null | undefined): string =>
  score === null || score === undefined ? copy.diff.noScore : score.toFixed(1)

export const formatDelta = (delta: number | null | undefined): string =>
  delta === null || delta === undefined
    ? ''
    : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}`

/** accepted, not accepted, or absent when the run did not record the face. */
export const faceState = (face: FaceOffFace | null): string => {
  if (!face) return copy.diff.state.absent
  return face.accepted ? copy.diff.state.accepted : copy.diff.state.recorded
}

export const iou = (a: FaceOffBox | null, b: FaceOffBox | null): number => {
  if (!a || !b) return 0
  const ix = Math.max(
    0,
    Math.min(a.left + a.width, b.left + b.width) - Math.max(a.left, b.left),
  )
  const iy = Math.max(
    0,
    Math.min(a.top + a.height, b.top + b.height) - Math.max(a.top, b.top),
  )
  const inter = ix * iy
  const union = a.width * a.height + b.width * b.height - inter
  return union <= 0 ? 0 : inter / union
}

export interface MergedFace<T> {
  bbox: FaceOffBox | null
  candidates: T[]
}

/**
 * The run checks each face once per scan and keeps a box per scan. Boxes that
 * overlap (IoU 0.5 or more) are one face, drawn and numbered once; its rows
 * still say which scan found each match.
 */
export const mergeFaces = <T extends {bbox: FaceOffBox | null}>(
  faces: T[],
): MergedFace<T>[] => {
  const merged: MergedFace<T>[] = []
  faces.forEach((f) => {
    const hit = f.bbox
      ? merged.find((m) => m.bbox && iou(m.bbox, f.bbox) >= 0.5)
      : undefined
    if (hit) hit.candidates.push(f)
    else merged.push({bbox: f.bbox, candidates: [f]})
  })
  return merged
}

/**
 * Pairs each face of a run with the same face in another run, as media pairs
 * a run with the run before: the same scan, photo and person, with boxes that
 * overlap (IoU 0.5 or more). A flip is a change in acceptance, counting a face
 * the other run did not record as not accepted. A shift is a score move
 * larger than `band` that did not cross the cut-off.
 */
export const compareFaces = (
  faces: FaceOffRunFace[],
  other: FaceOffFace[],
  band: number,
): FaceOffRunFace[] => {
  const used = other.map(() => false)
  return faces.map((f) => {
    const j = other.findIndex(
      (o, k) =>
        !used[k] &&
        o.path === f.path &&
        o.photoId === f.photoId &&
        o.personId === f.personId &&
        iou(o.bbox, f.bbox) >= 0.5,
    )
    if (j >= 0) used[j] = true
    const previous = j >= 0 ? other[j] : null
    let change: FaceOffRunFaceChange = ''
    if ((previous?.accepted ?? false) !== f.accepted) change = 'flip'
    else if (
      previous?.score != null &&
      f.score !== null &&
      Math.abs(f.score - previous.score) > band
    )
      change = 'shift'
    return {...f, previous, change}
  })
}
